import type { MouseEvent } from 'react'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { EIconSize, Icon } from '@frontend/pole-ui/lib/components/Icon'
import classNames from 'classnames'
import type { NavLink } from '@/components/composed/Header/components/NavLinks/interfaces'
import { ENavLink } from '@/components/composed/Header/components/NavLinks/interfaces'
import { addCommentForConsultingForm } from '@/components/composed/InstallmentCalculator/hooks'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { LandingConsultingModal } from '@/components/composed/modals'
import type { IFinancingConsultingModalProps } from '@/components/composed/modals/LandingConsultingModal/types'
import { isAnyPermissionIntersected } from '@/logic/auth/helpers'
import { getUserPermissions } from '@/logic/auth/reducer'
import type { RootState } from '@/redux/interfaces'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { links, mobileLinks } from './constants'
import './styles.scss'

addModalToWellKnown({ LandingConsultingModal })

const mapState = (state: RootState) => ({
  userPermissions: getUserPermissions(state),
})

const NavLinks: React.FC<{ classname?: string; isEnabledSubLinks?: boolean }> = ({ classname, isEnabledSubLinks = false }) => {
  const [isActiveSubLinks, setIsActiveSubLinks] = useState(false)
  const { userPermissions } = useSelector(mapState)
  const { isMobile, isTablet, isDesktop } = useDeviceType()
  const currentLinks = isMobile || isTablet ? mobileLinks : links
  const dispatch = useDispatch()

  const handleShowSubMenu = (isHasSubLinks?: boolean) => {
    if (isHasSubLinks) setIsActiveSubLinks(true)
  }

  const handleHideSubMenu = (isHasSubLinks?: boolean) => {
    if (isHasSubLinks) setIsActiveSubLinks(false)
  }

  const handleClickSublink = (event: MouseEvent, sublink: NavLink) => {
    if (sublink.id === ENavLink.FINANCING_CONSULT) {
      setTimeout(() => {
        dispatch(
          openModal({
            options: { dialogId: 'LandingConsultingModal' },
            contentOptions: {
              requestMapper: addCommentForConsultingForm,
            } as IFinancingConsultingModalProps['options'],
          }),
        )
      })
    }
  }

  return (
    <ul className={classNames('nav-list', classname)}>
      {currentLinks.map(({ text, url, icon, permissions, subLinks }) => {
        const isHasSubLinks = subLinks && subLinks?.length > 0
        const isShowSubMenuActive = isHasSubLinks && isActiveSubLinks && isDesktop && isEnabledSubLinks

        if (Array.isArray(permissions) && !isAnyPermissionIntersected(permissions, userPermissions)) return null

        return (
          <li
            key={text}
            className="nav-list-item"
            onMouseEnter={() => handleShowSubMenu(isHasSubLinks)}
            onMouseLeave={() => handleHideSubMenu(isHasSubLinks)}
          >
            <CommonLink url={url} spanIfEmpty className="nav-list-item-link" activeClassName="nav-list-item-link_active">
              <span className="nav-list-item-link__icon">
                <Icon component={icon} size={EIconSize.M} />
              </span>
              <span className="nav-list-item-link__text">{text}</span>
            </CommonLink>

            {isShowSubMenuActive && (
              <div
                className="nav-list-item-submenu"
                onMouseEnter={() => handleShowSubMenu(isHasSubLinks)}
                onMouseLeave={() => handleHideSubMenu(isHasSubLinks)}
              >
                <div className="nav-list-item-submenu__wrapper">
                  {subLinks.map(sublink => (
                    <CommonLink
                      key={sublink.text}
                      url={sublink.url}
                      className={classNames('nav-list-item-submenu-link', { 'nav-list-item-submenu-link--accent': sublink.isAccent })}
                      onClick={event => handleClickSublink(event, sublink)}
                    >
                      <div className="nav-list-item-submenu-link__icon">
                        <Icon component={sublink.icon} size={EIconSize.M} />
                      </div>

                      <span className="nav-list-item-submenu-link__text">
                        <div className="nav-list-item-submenu-link__title">{sublink.text}</div>
                        <div className="nav-list-item-submenu-link__subtitle">{sublink.subText}</div>
                      </span>
                    </CommonLink>
                  ))}
                </div>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export default NavLinks
